import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import { useChartTheme } from '@/components/charts/theme';
import type { BioscopeDataQuality, WarningMetric, WarningTier } from '@/domain/feedingDashboard';
import { parseLocale } from '@/utils/localeFormatters';

import { FeedingChartPanel } from './FeedingChartPanel/FeedingChartPanel';
import {
    FEEDING_COLORS,
    toneColor,
    toneOutline,
    WARNED_BAR_OUTLINE_WIDTH,
    warningPalette,
} from './feedingChartColors';
import { PenChartKey } from './PenChartKey';

import styles from './PenBarChart.module.scss';

export interface PenBarValue {
    /** The pen's label, unique among the values: it is also the bar's category key. */
    penName: string;
    pms: number | null;
    bioscope: number | null;
    /**
     * Each bar's state from the shared warning rule, decided by the caller against the
     * pen's own target. The chart only paints it, so a bar cannot disagree with the pen
     * table cell that prints the same value.
     */
    pmsTier: WarningTier;
    bioscopeTier: WarningTier;
}

interface PenBarChartProps {
    title: string;
    /** The value axis title: the metric alone, since the panel title already says the rest. */
    axisLabel: string;
    /** The configured planning-system name, for the key's bar order. */
    systemName: string;
    /** Bioscope data-quality state for the req 11 indicator. */
    quality?: BioscopeDataQuality;
    values: PenBarValue[];
    /** Legend labels; the planning-system one carries the configured name (IPS 40). */
    seriesLabels: { pms: string; bioscope: string };
    /**
     * Site target, drawn as the single reference line. Deliberately not per pen: one
     * line per bar would be unreadable, and the site value is the meaningful shared
     * reference. Individual bars still take their state from their own pen's target.
     */
    target: number;
    /** Which target `target` is, so the key names it as the cards and the table do. */
    metric: WarningMetric;
    /** Decimals on the axis and in tooltips. FCR wants 2, RGI 1. */
    decimals?: number;
    /**
     * Shown in place of the bars when no pen has a value (IPS 2.1.4). Decided from the
     * values rather than the visible series, since hiding the only series with values
     * through the legend does not mean the pens have none.
     */
    noDataMessage: string;
    /** Caption lines under the chart, each explaining why a series cannot have values. */
    notes?: string[];
    loading?: boolean;
    error?: unknown;
}

/**
 * Grouped planning-system vs Bioscope bar chart per pen with a target reference line
 * (IPS 2.4, 2.5).
 *
 * Built on the shared `MetricChart` layer, so it inherits the app's legend, loading,
 * error and empty states rather than reimplementing them. The x axis is pen names rather
 * than days, hence `xAxisKind: 'category'`; `FeedingChartPanel` stays the wrapper because
 * MetricChart deliberately owns no title.
 */
export function PenBarChart(props: PenBarChartProps) {
    const {
        title,
        axisLabel,
        systemName,
        values,
        seriesLabels,
        target,
        metric,
        decimals = 2,
        noDataMessage,
        notes = [],
        loading,
        error,
        quality,
    } = props;
    const { i18n } = useTranslation();
    // Read once per mount: the tokens are static for a page load, and reading computed
    // style on every render would force a style recalculation each time.
    // Re-derived when the theme switches; the outline ink differs between themes.
    const chartTheme = useChartTheme();
    const palette = useMemo(() => warningPalette(chartTheme), [chartTheme]);
    const pmsHasValue = values.some((value) => isPlottable(value.pms));
    const bioscopeHasValue = values.some((value) => isPlottable(value.bioscope));
    const hasValue = pmsHasValue || bioscopeHasValue;
    const hasWarning = values.some(
        (value) => value.pmsTier !== 'none' || value.bioscopeTier !== 'none'
    );

    // Pen labels double as the category keys every series joins against. The join keeps
    // the last value for a repeated key while the per-bar colours stay positional, so a
    // shared name would draw one pen's value in another pen's colour.
    const days = values.map((value) => value.penName);
    const points = (selector: (value: PenBarValue) => number | null) =>
        values.map((value) => ({ day: value.penName, value: selector(value) }));

    const spec: MetricChartSpec<'bar'> = {
        kind: 'bar',
        xAxisKind: 'category',
        days,
        locale: parseLocale(i18n.language),
        series: [
            {
                ...emptySeriesVisibility('pms', pmsHasValue),
                label: seriesLabels.pms,
                color: FEEDING_COLORS.pms,
                data: points((value) => value.pms),
                barColors: values.map((value) =>
                    toneColor(value.pmsTier, FEEDING_COLORS.pms, palette)
                ),
                barOutlines: {
                    colors: values.map((value) => toneOutline(value.pmsTier, palette)),
                    width: WARNED_BAR_OUTLINE_WIDTH,
                },
                decimals,
            },
            {
                ...emptySeriesVisibility('bioscope', bioscopeHasValue),
                label: seriesLabels.bioscope,
                color: FEEDING_COLORS.bioscope,
                data: points((value) => value.bioscope),
                barColors: values.map((value) =>
                    toneColor(value.bioscopeTier, FEEDING_COLORS.bioscope, palette)
                ),
                barOutlines: {
                    colors: values.map((value) => toneOutline(value.bioscopeTier, palette)),
                    width: WARNED_BAR_OUTLINE_WIDTH,
                },
                decimals,
            },
        ],
        // From zero, because a bar is read by its length: RGI 95 against 100 becomes
        // two near-equal bars, and the target line and the warning fill carry the
        // shortfall (U6). A negative bFCR (fish losing weight before harvest) still
        // draws: Chart.js 4.4 pulls the axis to zero only when every value shares a sign.
        left: {
            label: axisLabel,
            beginAtZero: true,
            grace: '5%',
            valueDecimals: decimals,
            tickDecimals: decimals,
        },
        // No label in the plot, where the dashed line struck through it and the bars
        // covered it: the key under the chart states the target instead.
        referenceLines: [{ key: 'target', value: target, color: FEEDING_COLORS.target }],
    };

    const chart = useMetricChart(spec);

    return (
        <FeedingChartPanel title={title} quality={quality}>
            <MetricChart
                kind="bar"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                isLoading={loading}
                error={error}
                noDataMessage={hasValue ? undefined : noDataMessage}
            />
            {hasValue ? (
                <PenChartKey
                    kind="target"
                    target={target}
                    metric={metric}
                    lineColor={chartTheme.color(FEEDING_COLORS.target)}
                    warned={
                        hasWarning
                            ? { palette, systemName, paired: pmsHasValue && bioscopeHasValue }
                            : undefined
                    }
                />
            ) : null}
            {notes.map((note) => (
                <p key={note} className={styles.note}>
                    {note}
                </p>
            ))}
        </FeedingChartPanel>
    );
}

/**
 * A series with no value for any shown pen would still reserve a slot beside every bar of
 * the other, an empty bar that reads as belonging to the figure next to it. It starts
 * hidden then, kept in the legend to switch back on; one value on any shown pen shows it.
 *
 * The legend stores a switch as a flip of the default, so the empty series gets a key of
 * its own: a flip made while it was empty must not hide it once a refresh brings values.
 */
function emptySeriesVisibility(
    key: 'pms' | 'bioscope',
    hasValue: boolean
): { key: string; hiddenByDefault: boolean } {
    return { key: hasValue ? key : `${key}Empty`, hiddenByDefault: !hasValue };
}

function isPlottable(value: number | null): boolean {
    return value != null && Number.isFinite(value);
}
